/**
 * Client-side image preparation, before anything is uploaded.
 *
 * ── Why this file exists at all ──────────────────────────────────────────────
 *
 * Three separate reasons, and each on its own would justify it:
 *
 *  1. **HEIC.** Every photograph an iPhone takes on default settings is HEIC.
 *     `services/api/src/uploads.ts` sniffs the FIRST BYTES and accepts exactly
 *     jpeg, png, gif, webp and pdf — deny by default, on purpose. A HEIC file
 *     posted straight from a phone is a 415 with a message about file types,
 *     standing in a yard, in the sun, with the seller waiting. Decoding it here
 *     and handing the server a JPEG is the whole fix, and §3 already recorded
 *     that it needs no change to `uploads.ts` because every upload in this
 *     application is user-initiated.
 *
 *  2. **Size.** A 12-megapixel photo is 3–6MB. `uploads.ts` caps at 10MB, so
 *     they would mostly *work* — over Vieques LTE, at maybe 300kB/s, one photo
 *     is twenty seconds and an inspection is twenty photos. Downscaling the long
 *     edge to 2048px at q=0.82 lands them around 300–500kB.
 *
 *  3. **EXIF, and §14.2.** A re-encode through a canvas drops every metadata
 *     block, GPS included. §14.2 puts the parcel's coordinates on the
 *     never-logged list; a photo of the structure carrying the exact position of
 *     the land in its header is that same leak wearing a different hat. The
 *     server strips EXIF too (`documents.ts`), and doing it in both places is
 *     correct: this one also stops the bytes leaving the phone.
 *
 * ── The orientation trap ─────────────────────────────────────────────────────
 *
 * EXIF carries the rotation. Strip the metadata without APPLYING it and every
 * portrait photo lands on its side — the classic "why is my roof sideways" bug.
 * `createImageBitmap(blob, { imageOrientation: 'from-image' })` rotates the
 * pixels first; the `<img>` fallback below relies on the browser having done the
 * same thing during decode, which every current engine does.
 */

/** The long edge, after downscaling. Enough to read a serial plate, small enough to send. */
export const MAX_EDGE = 2048
/** JPEG quality. 0.82 is where photographs stop getting visibly better per byte. */
export const JPEG_QUALITY = 0.82
/** Refuse before decoding. A 40MB burst-mode file is a mistake, not a photo of a roof. */
export const MAX_INPUT_BYTES = 40 * 1024 * 1024

export interface PreparedImage {
  /** Always image/jpeg, whatever went in. */
  blob: Blob
  width: number
  height: number
  /** So the UI can say "4.1MB → 380kB" rather than claiming something happened. */
  originalBytes: number
  bytes: number
  originalName: string
}

export type ImageFailure =
  | 'too_large'
  | 'not_an_image'
  | 'decode_failed'
  | 'heic_unsupported'
  | 'encode_failed'
  | 'no_canvas'

export class ImagePrepError extends Error {
  readonly code: ImageFailure
  constructor(code: ImageFailure, message: string) {
    super(message)
    this.name = 'ImagePrepError'
    this.code = code
  }
}

/**
 * HEIC/HEIF, by the container box rather than by the extension or the MIME type.
 *
 * iOS often hands a share or a file input a `File` with an empty `type`, so
 * trusting `file.type` alone reports "not HEIC" for the exact files that are.
 * The ISO-BMFF `ftyp` box sits at byte 4 and its brand is at byte 8.
 */
export async function looksHeic(file: Blob): Promise<boolean> {
  const declared = (file.type || '').toLowerCase()
  if (declared.includes('heic') || declared.includes('heif')) return true
  try {
    const head = new Uint8Array(await file.slice(0, 16).arrayBuffer())
    if (head.length < 12) return false
    const ftyp = String.fromCharCode(head[4] ?? 0, head[5] ?? 0, head[6] ?? 0, head[7] ?? 0)
    if (ftyp !== 'ftyp') return false
    const brand = String.fromCharCode(head[8] ?? 0, head[9] ?? 0, head[10] ?? 0, head[11] ?? 0)
    return ['heic', 'heix', 'hevc', 'heim', 'heis', 'hevm', 'hevs', 'mif1', 'msf1'].includes(brand)
  } catch {
    // A Blob that will not even yield its first sixteen bytes is not something
    // we can classify; let the decoder produce the real error.
    return false
  }
}

function scaleTo(width: number, height: number, maxEdge: number): { w: number; h: number } {
  const longest = Math.max(width, height)
  if (longest <= maxEdge) return { w: width, h: height }
  const factor = maxEdge / longest
  // At least 1px on each axis: a 4000x1 panorama must not round to zero and
  // throw inside the canvas.
  return { w: Math.max(1, Math.round(width * factor)), h: Math.max(1, Math.round(height * factor)) }
}

interface Decoded {
  source: CanvasImageSource
  width: number
  height: number
  release: () => void
}

async function decode(file: Blob): Promise<Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close() }
    } catch {
      // Falls through to the <img> path. Safari has shipped createImageBitmap
      // options later than the function itself more than once, so a throw here
      // is not proof the file is undecodable.
    }
  }

  if (typeof Image !== 'function' || typeof URL?.createObjectURL !== 'function') {
    throw new ImagePrepError('decode_failed', 'This browser cannot decode that image.')
  }

  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new ImagePrepError('decode_failed', 'That image could not be opened.'))
      el.src = url
    })
    const width = img.naturalWidth || img.width
    const height = img.naturalHeight || img.height
    if (!width || !height) throw new ImagePrepError('decode_failed', 'That image has no size.')
    return { source: img, width, height, release: () => URL.revokeObjectURL(url) }
  } catch (err) {
    URL.revokeObjectURL(url)
    throw err
  }
}

function makeCanvas(w: number, h: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  if (typeof document === 'undefined') throw new ImagePrepError('no_canvas', 'No canvas is available here.')
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new ImagePrepError('no_canvas', 'This browser refused a 2D canvas.')
  return { canvas, ctx }
}

function toJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    // toBlob, not toDataURL: a data URL is a base64 STRING of the whole image
    // held in JS memory, ~33% larger, and on a phone with twenty of them open
    // it is what actually kills the tab.
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new ImagePrepError('encode_failed', 'That photo could not be saved.'))
      },
      'image/jpeg',
      JPEG_QUALITY,
    )
  })
}

/**
 * Decode, rotate, downscale, re-encode as JPEG. The one thing every photo in
 * this application passes through before it reaches the queue.
 *
 * Throws `ImagePrepError`, so a caller can tell "this device cannot read HEIC"
 * (fixable: Settings → Camera → Formats → Most Compatible) from "that is not an
 * image" (not fixable by trying again).
 */
export async function prepareImage(file: File | Blob, maxEdge = MAX_EDGE): Promise<PreparedImage> {
  const originalName = file instanceof File ? file.name : 'photo.jpg'
  const originalBytes = file.size

  if (originalBytes > MAX_INPUT_BYTES) {
    throw new ImagePrepError('too_large', 'That file is too big to be a photo.')
  }
  if (originalBytes === 0) {
    throw new ImagePrepError('not_an_image', 'That file is empty.')
  }

  const heic = await looksHeic(file)

  let decoded: Decoded
  try {
    decoded = await decode(file)
  } catch (err) {
    if (heic) {
      // Named separately because the fix is a phone setting, and a user told
      // "that image could not be opened" will try the same photo again.
      throw new ImagePrepError(
        'heic_unsupported',
        'This device cannot read HEIC photos. On iPhone: Settings → Camera → Formats → Most Compatible.',
      )
    }
    throw err instanceof ImagePrepError ? err : new ImagePrepError('decode_failed', 'That image could not be opened.')
  }

  try {
    const { w, h } = scaleTo(decoded.width, decoded.height, maxEdge)
    const { canvas, ctx } = makeCanvas(w, h)
    ctx.drawImage(decoded.source, 0, 0, w, h)
    const blob = await toJpeg(canvas)
    return { blob, width: w, height: h, originalBytes, bytes: blob.size, originalName }
  } finally {
    decoded.release()
  }
}
